import {useMemo, useState} from '@wordpress/element';
import {useLanding} from "../../store/landing";
import {ProductCard, ProductCardSkeleton} from "../common/ProductCard";
import {buildViewAllHref} from "./landingUtils";

const TabbedProductGridSkeleton = () => (
    <div className="mb-16">
        <div className="flex items-center border-b border-base-200">
            <div className="flex gap-4 overflow-x-auto no-scrollbar flex-1">
                {Array.from({length: 4}).map((_, i) => (
                    <div key={i} className="pb-4 px-4">
                        <div className="skeleton h-6 w-24"/>
                    </div>
                ))}
            </div>
            <div className="skeleton h-4 w-16 pl-4 shrink-0"/>
        </div>
        <div className="flex overflow-x-auto flex-nowrap gap-6 py-8 no-scrollbar">
            {Array.from({length: 4}).map((_, i) => (
                <div key={i} className="w-64 shrink-0">
                    <ProductCardSkeleton/>
                </div>
            ))}
        </div>
    </div>
);

export const TabbedProductGrid = ({loading}) => {
    const {recommendations, categories} = useLanding();
    const [activeTab, setActiveTab] = useState(0);

    const tabs = useMemo(() => {
        const r = Array.isArray(recommendations) ? recommendations : [];
        const c = Array.isArray(categories) ? categories : [];
        return [...r, ...c];
    }, [recommendations, categories]);

    if (loading) return <TabbedProductGridSkeleton/>;
    if (tabs.length === 0) return null;

    const activeSection = tabs[activeTab] || tabs[0];
    const products = Array.isArray(activeSection?.products) ? activeSection.products : [];
    const viewAllHref = buildViewAllHref(activeSection);

    const hideViewAll = !viewAllHref ||
        ['Most Viewed', 'Trending Viewed', 'Trending'].some(n =>
            activeSection.name?.toLowerCase().includes(n.toLowerCase())
        );

    return (
        <div className="mb-16">
            <div className="flex items-center border-b border-base-200">
                <div className="flex overflow-x-auto no-scrollbar flex-1">
                    {tabs.map((tab, idx) => (
                        <button
                            key={idx}
                            className={`pb-2 px-4 cursor-pointer text-lg font-bold whitespace-nowrap transition-all border-b-2 -mb-px ${
                                activeTab === idx
                                    ? 'border-[#FE7C22] pt-orange'
                                    : 'border-transparent text-base-content/40 hover:text-base-content/60'
                            }`}
                            onClick={() => setActiveTab(idx)}
                        >
                            {tab.name}
                        </button>
                    ))}
                </div>

                {!hideViewAll && (
                    <a
                        href={viewAllHref}
                        className="pl-4 pb-2 text-sm font-bold pt-blue whitespace-nowrap shrink-0 hover:opacity-80 transition-opacity"
                    >
                        See All →
                    </a>
                )}
            </div>

            <div className="flex overflow-x-auto flex-nowrap gap-6 py-8 no-scrollbar scroll-smooth">
                {products.slice(0, 12).map((product, idx) => (
                    <div key={(product.sku || idx) + '-tabbed'} className="w-64 shrink-0">
                        <ProductCard product={product}/>
                    </div>
                ))}
            </div>
        </div>
    );
};